import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  LineChart, ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { useDarkMode } from "../hooks/useDarkMode";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtPct, applyMult } from "./theme";
import { SkeletonCard, NoData, SectionTitle, SrTable, ExperimentalBadge, BreakBadge, NoteBadge } from "./components";
import PulseLayout from "./PulseLayout";
import { pulseGet } from "./api";
import { showsBand, showsMedian, ForecastNote, FC_BAND } from "./forecast";

const INDICATORS = [
  { code: "intl_arrivals", freq: "A" },
  { code: "travel_receipts_usd", freq: "A" },
  { code: "travel_receipts_ncu", freq: "A" },
  { code: "tourism_gdp_share", freq: "A" },
  { code: "tourism_employment_share", freq: "A" },
  { code: "intl_departures", freq: "A" },
  { code: "outbound_expenditure_usd", freq: "A" },
  { code: "nights_total", freq: "M" },
  { code: "platform_nights", freq: "M" },
];

const COLORS = ["#006874", "#C2410C", "#7C3AED", "#0369A1"];

export default function ComparePage() {
  const [dm] = useDarkMode();
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState(["ITA", "ESP"]);
  const [indicator, setIndicator] = useState("intl_arrivals");
  const [source, setSource] = useState(null);
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [index2019, setIndex2019] = useState(false);

  useEffect(() => {
    pulseGet("countries").then((d) => setCountries(d.countries || [])).catch(() => {});
  }, []);

  const meta = INDICATORS.find((i) => i.code === indicator);

  useEffect(() => {
    let on = true;
    setLoading(true);
    pulseGet("series", { indicator, countries: selected.join(","), freq: meta.freq, with_forecast: 1, lang })
      .then((d) => {
        if (!on) return;
        setResp(d);
        setSource((prev) => (prev && d.sources.includes(prev) ? prev : d.default_source));
      })
      .catch(() => { if (on) setResp(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [indicator, selected, meta.freq, lang]);

  const activeSeries = useMemo(
    () => (resp?.series || []).filter((s) => s.source_org === source),
    [resp, source]);

  // Merge series into one recharts dataset: {period, ITA: v, ESP: v, ...}; forecast
  // (M4) appends `${iso}_fc` dashed medians and, with ONE country, the 10-90 band.
  const forecasts = useMemo(() => activeSeries.filter((s) => showsBand(s.forecast) && !index2019), [activeSeries, index2019]);
  const chartData = useMemo(() => {
    const rows = {};
    activeSeries.forEach((s) => {
      const base2019 = index2019
        ? (s.points.find((p) => p.period === "2019") || {}).value
        : null;
      s.points.forEach((p) => {
        const v = index2019
          ? (base2019 ? (p.value / base2019) * 100 : null)
          : applyMult(p.value, s.unit_mult);
        if (v === null || v === undefined) return;
        (rows[p.period] = rows[p.period] || { period: p.period })[s.ref_area] = v;
      });
    });
    forecasts.forEach((s) => {
      const fc = s.forecast;
      const mult = Math.pow(10, parseInt(s.unit_mult || 0, 10) || 0);
      const last = s.points[s.points.length - 1];
      if (last && showsMedian(fc) && rows[last.period]) rows[last.period][`${s.ref_area}_fc`] = applyMult(last.value, s.unit_mult);
      fc.points.forEach((p) => {
        const row = (rows[p.period] = rows[p.period] || { period: p.period, forecast: true });
        if (showsMedian(fc)) row[`${s.ref_area}_fc`] = p.q50 * mult;
        if (selected.length === 1) row.band = [p.q10 * mult, p.q90 * mult];
      });
    });
    return Object.values(rows).sort((a, b) => (a.period > b.period ? 1 : -1));
  }, [activeSeries, forecasts, index2019, selected]);

  const isExperimental = activeSeries.some((s) => s.data_type === "experimental");
  const filteredOptions = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return countries
      .filter((c) => !selected.includes(c.iso3))
      .filter((c) => !needle || c.label.toLowerCase().includes(needle) || c.iso3.toLowerCase().includes(needle))
      .slice(0, 12);
  }, [countries, selected, q]);

  const fmtVal = (v) => (index2019 ? fmtCompact(v, lang) : fmtCompact(v, lang));

  return (
    <PulseLayout active="compare">
      <div className="pulse-fade-up">
        <h1 className="pulse-serif text-4xl sm:text-5xl font-medium" data-testid="compare-title">{t("cmp_title")}</h1>
        <p className="text-sm mt-2 mb-6" style={{ color: T.muted }}>{t("cmp_sub")}</p>

        {/* Controls */}
        <div className="flex flex-wrap items-start gap-4 mb-6 p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
          <div className="min-w-[220px]">
            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: T.muted }} htmlFor="cmp-country-input">
              {t("cmp_max")}
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {selected.map((iso, i) => (
                <span key={iso} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-1"
                  data-testid={`compare-chip-${iso}`}
                  style={{ background: COLORS[i % COLORS.length], color: "#fff", borderRadius: 2 }}>
                  {iso}
                  {selected.length > 1 && (
                    <button onClick={() => setSelected(selected.filter((s) => s !== iso))} aria-label={`Remove ${iso}`}
                      data-testid={`compare-remove-${iso}`}>
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
            {selected.length < 4 && (
              <div className="relative">
                <input id="cmp-country-input" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder={t("cmp_add")} data-testid="compare-country-input"
                  className="w-full px-3 py-2 text-sm outline-none"
                  style={{ background: T.bg, border: `1px solid ${T.border}`, color: T.text, borderRadius: 2 }} />
                {q && filteredOptions.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 top-full mt-1 max-h-52 overflow-y-auto"
                    style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                    {filteredOptions.map((c) => (
                      <button key={c.iso3} data-testid={`compare-option-${c.iso3}`}
                        onClick={() => { setSelected([...selected, c.iso3]); setQ(""); }}
                        className="w-full text-left px-3 py-2 text-sm pulse-row-hover"
                        style={{ color: T.text, borderBottom: `1px solid ${T.border}` }}>
                        {c.label} <span className="text-[10px]" style={{ color: T.muted }}>{c.iso3}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: T.muted }} htmlFor="cmp-indicator">
              {t("cmp_indicator")}
            </label>
            <select id="cmp-indicator" value={indicator} data-testid="compare-indicator-select"
              onChange={(e) => { setIndicator(e.target.value); setSource(null); setIndex2019(false); }}
              className="px-3 py-2 text-sm outline-none"
              style={{ background: T.bg, border: `1px solid ${T.border}`, color: T.text, borderRadius: 2 }}>
              {INDICATORS.map((i) => (
                <option key={i.code} value={i.code}>{t(`ind_${i.code}`)}</option>
              ))}
            </select>
          </div>

          {resp?.sources?.length > 1 && (
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: T.muted }}>{t("cmp_source")}</span>
              <div className="flex gap-1" role="group" aria-label={t("cmp_source")} data-testid="compare-source-switch">
                {resp.sources.map((s) => (
                  <button key={s} onClick={() => setSource(s)} aria-pressed={source === s}
                    data-testid={`compare-source-${s}`}
                    className="text-[11px] font-semibold px-2 py-1.5"
                    style={{
                      background: source === s ? T.brand : "transparent",
                      color: source === s ? "#fff" : T.muted,
                      border: `1px solid ${source === s ? T.brand : T.border}`, borderRadius: 2,
                    }}>
                    {t(`src_${s}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {meta.freq === "A" && (
            <div>
              <span className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: T.muted }}>&nbsp;</span>
              <button onClick={() => setIndex2019(!index2019)} aria-pressed={index2019} data-testid="compare-index2019-toggle"
                className="text-[11px] font-semibold px-2 py-1.5"
                style={{
                  background: index2019 ? T.brand : "transparent",
                  color: index2019 ? "#fff" : T.muted,
                  border: `1px solid ${index2019 ? T.brand : T.border}`, borderRadius: 2,
                }}>
                {t("cmp_index2019")}
              </button>
            </div>
          )}
          {isExperimental && <div className="self-end pb-1"><ExperimentalBadge dm={dm} /></div>}
        </div>

        {/* Overlay chart */}
        <section className="mb-8">
          <SectionTitle dm={dm}>{t("cmp_overlay")} — {t(`ind_${indicator}`)}</SectionTitle>
          {loading && <SkeletonCard h={320} dm={dm} />}
          {!loading && !chartData.length && <NoData dm={dm} h={160} testid="compare-no-data" />}
          {!loading && chartData.length > 0 && (
            <div className="p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="compare-overlay-chart">
              <div style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                    <CartesianGrid stroke={T.border} vertical={false} />
                    <XAxis dataKey="period" tick={{ fontSize: 10, fill: T.muted }} interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: T.border }} />
                    <YAxis tickFormatter={(v) => (index2019 ? Math.round(v) : fmtCompact(v, lang))} tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={false} width={52} />
                    <Tooltip formatter={(v) => (index2019 ? `${Math.round(v)} (2019=100)` : fmtCompact(v, lang))}
                      contentStyle={{ background: dm ? "#0F1115" : "#111827", border: `1px solid ${T.border}`, borderRadius: 2, fontSize: 12, color: "#F3F4F6" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {forecasts.length > 0 && selected.length === 1 && (
                      <Area type="monotone" dataKey="band" name={t("fc_band")} stroke="none" fill={FC_BAND} fillOpacity={1}
                        legendType="none" isAnimationActive={false} />
                    )}
                    {selected.map((iso, i) => (
                      <Line key={iso} type="monotone" dataKey={iso} name={iso} stroke={COLORS[i % COLORS.length]}
                        strokeWidth={2} dot={false} connectNulls isAnimationActive={true} animationDuration={400} />
                    ))}
                    {forecasts.filter((s) => showsMedian(s.forecast)).map((s) => (
                      <Line key={`${s.ref_area}_fc`} type="monotone" dataKey={`${s.ref_area}_fc`} name={`${s.ref_area} · ${t("fc_median")}`}
                        stroke={COLORS[selected.indexOf(s.ref_area) % COLORS.length]} strokeWidth={2} strokeDasharray="5 4"
                        dot={false} connectNulls={false} isAnimationActive={false} />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[10px] mt-2 font-semibold uppercase tracking-wider" style={{ color: T.muted }}>
                {t("source")}: {t(`src_${source}`)}{index2019 ? ` · ${t("cmp_index2019")}` : ""}
              </p>
              {activeSeries.filter((s) => s.area_note).map((s) => (
                <div key={`note-${s.ref_area}`} className="flex items-center gap-2 mt-1 flex-wrap" data-testid={`compare-note-${s.ref_area}`}>
                  <span className="text-[10px] font-bold" style={{ color: T.muted }}>{s.ref_area}</span>
                  <NoteBadge note={s.area_note} dm={dm} label={t("note_badge_tur")} testid={`compare-note-badge-${s.ref_area}`} />
                  <span className="text-[10px]" style={{ color: T.muted }}>{(s.area_note.periods || []).join(" · ")} · {lang === "it" ? s.area_note.note_it : s.area_note.note}</span>
                </div>
              ))}
              {activeSeries.filter((s) => s.series_break).map((s) => (
                <div key={`brk-${s.ref_area}`} className="flex items-center gap-2 mt-1 flex-wrap" data-testid={`compare-break-${s.ref_area}`}>
                  <span className="text-[10px] font-bold" style={{ color: T.muted }}>{s.ref_area}</span>
                  <BreakBadge brk={s.series_break} dm={dm} testid={`compare-break-badge-${s.ref_area}`} />
                  <span className="text-[10px]" style={{ color: T.muted }}>{s.series_break.period}+ · {lang === "it" ? s.series_break.reason_it : s.series_break.reason}</span>
                </div>
              ))}
              {!index2019 && activeSeries.filter((s) => s.forecast).map((s) => (
                <div key={`fcn-${s.ref_area}`} className="flex items-center gap-2 flex-wrap" data-testid={`compare-forecast-${s.ref_area}`}>
                  <span className="text-[10px] font-bold" style={{ color: T.muted }}>{s.ref_area}</span>
                  <ForecastNote fc={s.forecast} dm={dm} testid={`compare-forecast-note-${s.ref_area}`} />
                </div>
              ))}
              <SrTable caption={`${t("cmp_overlay")} — ${t(`ind_${indicator}`)}`}
                columns={[t("rank_col_year"), ...selected]}
                rows={chartData.map((r) => [r.period, ...selected.map((iso) => (r[iso] !== undefined ? fmtVal(r[iso]) : "—"))])} />
            </div>
          )}
        </section>

        {/* Small multiples */}
        {!loading && activeSeries.length > 1 && (
          <section className="mb-4">
            <SectionTitle dm={dm}>{t("cmp_small_multiples")}</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ gap: 1, background: T.border, border: `1px solid ${T.border}` }}>
              {activeSeries.map((s, i) => {
                const idx = selected.indexOf(s.ref_area);
                const data = s.points.map((p) => ({ period: p.period, value: applyMult(p.value, s.unit_mult) }));
                const last = data[data.length - 1];
                return (
                  <div key={`${s.source_org}-${s.ref_area}`} className="p-4" style={{ background: T.surface }} data-testid={`compare-multiple-${s.ref_area}`}>
                    <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: COLORS[idx % COLORS.length] }}>
                      {s.ref_area_label || s.ref_area}
                    </p>
                    <p className="pulse-num text-xl font-semibold mb-1">
                      {fmtCompact(last?.value, lang)}
                      <span className="text-[10px] font-normal ml-1.5" style={{ color: T.muted }}>{last?.period}</span>
                    </p>
                    <div style={{ height: 60 }} aria-hidden="true">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                          <Line type="monotone" dataKey="value" stroke={COLORS[idx % COLORS.length]} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </PulseLayout>
  );
}
